import React, { FC, useEffect, useState } from 'react';
import { SelfieVerification } from 'selfie-verification';
import ReactModal from 'react-modal';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_FAMILIES } from '../../../theme/material/constants';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import colors from '../../../theme/colors';
import JoinSkipButton from '../JoinSkipButton';
import breakpoints from '../../../theme/material/breakpoints';
import Spinner from '../../../atoms/loader/Spinner/Spinner';
import {
  facelivenessSubmitAction,
  setFaceLivenessStatus,
  setFaceLivenessVerifyLoader,
} from '../../../actions/profileActions';
import { useDisableBodyScroll } from '../../../hooks/useDisableBodyScroll';
import { isDarkMode } from '../../../utils/helpers';
import { getTextColor } from '../../../style';
import { RootState } from 'store';

const selfieVerificationOverwriteStyles = ({ isJoinFlow }) => {
  return css`
    [data-amplify-theme] {
      --amplify-colors-primary-80: #212121;
      --amplify-colors-font-primary: #595959;
      --amplify-colors-font-error: #e21d4a;
      --amplify-colors-background-primary: ${!isJoinFlow && isDarkMode() ? `#020D15` : `#f6f4f4`};
      --amplify-components-liveness-camera-module-background-color: ${!isJoinFlow && isDarkMode()
        ? `#020D15`
        : `#f6f4f4`};
      --amplify-components-alert-background-color: ${!isJoinFlow && isDarkMode() ? `#020D15` : `#f6f4f4`};
    }
    .amplify-liveness-freshness-canvas {
      position: absolute;
    }
    .amplify-liveness-match-indicator {
      width: min(70%, 220px);
    }
    .amplify-liveness-match-indicator__bar {
      background-color: #212121;
      height: 10px;
      border: 1px solid #212121;
    }
    .amplify-liveness-match-indicator__bar:after {
      background: #1c9ec9;
    }
    .amplify-liveness-toast--primary {
      padding: 15px 30px;
    }
    .amplify-liveness-toast--medium {
      background-color: #fff;
    }
    .amplify-flex.liveness-detector-check {
      gap: 0 !important;
    }
    .amplify-liveness-toast--primary .amplify-liveness-toast__message {
      font-family: Inter;
      font-size: 24px;
      font-style: normal;
      font-weight: 600;
      line-height: 16px;
    }
    .amplify-liveness-overlay {
      padding: 0;
    }
    .amplify-liveness-camera-module {
      border: ${!isJoinFlow && isDarkMode() ? `2px solid #020D15` : `2px solid #fff`};
      border-radius: 8px;
      overflow: hidden;
    }
    .amplify-liveness-toast--medium: {
      background-color: rgba(0, 0, 0, 0.3) !important;
      border-radius: 4px;
    }
    .amplify-button {
      margin-top: 20px;
      font-family: ${FONT_FAMILIES.primary};
    }
    .amplify-liveness-cancel-container {
      top: 0;
    }
    @media only screen and (min-width: 1024px) {
      .amplify-liveness-cancel-container,
      .amplify-liveness-cancel-button {
        display: none;
      }
    }
    @media (max-width: 430px) and (max-height: 932px) {
      .amplify-flex.liveness-detector-check {
        gap: 0 !important;
      }
    }
    @media only screen and (max-width: 1023px) {
      .amplify-liveness-freshness-canvas {
        position: fixed;
      }
      .amplify-liveness-toast--primary:has(.center-face-wrapper) {
        padding: 0;
        background-color: rgba(246, 244, 244, 0.4) !important;
      }
      .amplify-loveness-cancel-container {
        top: 0 !important;
        border: none;
      }
      .amplify-flex.liveness-detector-check {
        gap: 0 !important;
      }
      .amplify-liveness-recording-icon {
        background-color: #f2f2f2;
      }
      .amplify-button--link:active,
      .amplify-button--link:focus {
        box-shadow: none;
      }
      .amplify-liveness-camera-module {
        border: ${!isJoinFlow && isDarkMode() ? `3px solid #020D15` : `3px solid #f6f4f4`};
        border-radius: 0px;
      }
      .amplify-liveness-cancel-button {
        background-color: #8f8f8f;
        color: white;
        width: 35px;
        height: 35px;
        border-radius: calc(35px / 2);
      }
      .amplify-liveness-overlay-opaque {
        background-color: hsla(0, 0%, 0%, 0);
      }
    }
    .amplify-liveness-landscape-error-modal {
      padding: 20px;
    }
    position: relative;
    width: 100%;
    height: 100%;
    max-height: 100%;
    min-height: 100%;
    margin: auto;
    @media only screen and (max-width: 390px) and (max-height: 844px) {
      height: 60%;
    }
    @media only screen and (max-width: 430px) and (max-height: 932px) {
      height: 60%;
    }
  `;
};

const FaceLivenessContainer = styled.div`
  ${selfieVerificationOverwriteStyles}
`;

const CenterYourFaceWrapper = styled.div({
  maxWidth: 582,
  margin: 'auto',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  alignItems: 'center',
});

const CenterYourFaceText = styled('h6')<{ isJoinFlow: boolean }>(({ isJoinFlow }) => ({
  color: isJoinFlow ? colors.light.blue.primary : getTextColor().BLUE.color,
  fontFamily: FONT_FAMILIES.primary,
  fontSize: '24px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: 'normal',
  letterSpacing: '-0.48px',
  margin: 0,
}));

const SubTitle = styled('p')<{ isJoinFlow: boolean }>(({ isJoinFlow }) => ({
  color: isJoinFlow ? colors.light.gray.light : getTextColor().LIGHT_GRAY.color,
  fontFamily: FONT_FAMILIES.primary,
  fontSize: 15,
  fontWeight: 400,
  fontStyle: 'normal',
  lineHeight: '20px',
  margin: '5px 0px 10px 0px',
  textAlign: 'center',
}));

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const ModalWrapper = styled(ReactModal)({
  [breakpoints.between(580, 1024)]: {
    inset: 0,
    width: '100% !important',
    height: '919px !important',
  },
});

const ContentWrapper = styled('div')({
  width: '100%',
  height: '85%',
  '@media (max-width: 600px) and (max-height: 700px)': {
    height: '80%',
  },
  '@media (min-width: 1800px) and (min-height: 2100px)': {
    height: '80%',
  },
  '@media only screen and (max-width: 390px) and (max-height: 844px)': {
    height: '600px',
  },
  '@media only screen and (max-width: 430px) and (max-height: 932px)': {
    height: '600px',
  },
});

const BackButtonWrapper = styled('div')({
  display: 'inherit',
  textAlign: 'center',
  marginTop: 15,
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  marginBottom: '-30px',
  [breakpoints.down(1024)]: {
    display: 'none',
  },
});

const LoaderContainer = styled('div')<{ isJoinFlow: boolean; isMobile: boolean }>(({ isJoinFlow, isMobile }) => ({
  position: 'absolute',
  top: '-2px',
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: isMobile ? '100%' : '97%',
  zIndex: 99,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  background: isJoinFlow ? '#f6f4f4' : isDarkMode() ? '#020D15' : '#f6f4f4',
  borderRadius: '8px',
}));

const theme = {
  name: 'Face liveness verification theme',
  tokens: {
    colors: {
      background: {
        primary: {
          value: isDarkMode() ? `#020D15` : `#f6f4f4`,
        },
      },
      font: {
        default: {
          variable: {
            value: FONT_FAMILIES.primary,
          },
          static: {
            value: FONT_FAMILIES.primary,
          },
        },
      },
    },
    components: {
      button: {
        primary: {
          backgroundColor: {
            value: '#E21D4A',
          },
          borderColor: {
            value: '#E21D4A',
          },

          _hover: {
            backgroundColor: {
              value: '#E21D4A',
            },
            borderColor: {
              value: '#E21D4A',
            },
          },
          _active: {
            backgroundColor: {
              value: '#E21D4A',
            },
            borderColor: {
              value: '#E21D4A',
            },
          },
          _focus: {
            backgroundColor: {
              value: '#E21D4A',
            },
            borderColor: {
              value: '#E21D4A',
            },
          },
        },
      },
    },
  },
};

interface FaceLivenessProps {
  hashId: string;
  handleBack: () => void;
  customStyle?: React.CSSProperties;
  handleFaceLivelinessComplete?: any;
  loader: boolean;
  isJoinFlow: boolean;
}

const FaceLiveness: FC<FaceLivenessProps> = ({
  hashId,
  handleBack,
  customStyle,
  handleFaceLivelinessComplete,
  loader,
  isJoinFlow,
}) => {
  const { isMobile } = useMediaBreakpoints();
  const dispatch = useDispatch();

  useDisableBodyScroll(true);
  // State to store the detection status
  const [isVerifying, setIsVerifying] = useState<boolean>(false);

  const { isSVWallByGetSelfieVerified } = useSelector((state: RootState) => state.profile);

  useEffect(() => {
    if (!loader && isVerifying) {
      setIsVerifying(false);
    }
  }, [loader]);

  useEffect(() => {
    // Create a MutationObserver instance
    const observer = new MutationObserver((mutationsList, observer) => {
      for (const mutation of mutationsList) {
        // Check if the target element is added to the DOM
        if (mutation.type === 'childList') {
          const livenessHintElement = document.querySelector('.amplify-liveness-hint__text');

          if (livenessHintElement) {
            // Check if the text content of the element matches the expected text
            if (livenessHintElement?.textContent.includes('Verifying')) {
              // Set state to indicate the element was detected
              setIsVerifying(true);
              dispatch(setFaceLivenessVerifyLoader(true));
              // Once detected, disconnect the observer to stop observing
              observer.disconnect();
            }
          }
        }
      }
    });

    // Start observing the body (or a more specific container if applicable)
    observer.observe(document.body, {
      childList: true, // Watch for added or removed nodes
      subtree: true, // Watch all descendant nodes, not just direct children
    });

    // Clean up the observer when the component unmounts
    return () => {
      observer.disconnect();

      // Make body scrollable
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleAnalysisComplete = (results: any) => {
    if (!isEmpty(results.data)) {
      const origin = isJoinFlow ? 'Onboarding' : 'SV Wall';

      dispatch(facelivenessSubmitAction(hashId, results, origin));
      handleFaceLivelinessComplete();
    }
  };

  const setModalStyle = customStyle => {
    const customOverlay = customStyle?.overlay || {};
    const customContent = customStyle?.content || {};

    return {
      overlay: {
        position: 'absolute',
        zIndex: 10002,
        background: isJoinFlow ? 'none' : 'rgba(0,0,0,0.65)',
        inset: 0,
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        overflow: 'none',
        ...customOverlay,
      },
      content: {
        boxSizing: 'border-box',
        padding: isMobile ? 0 : '20px 30px',
        margin: '0 auto',
        outline: 'none',
        marginTop: isMobile ? 0 : '85px',
        overflow: 'unset',
        borderRadius: isMobile ? 0 : 8,
        width: 800,
        height: isJoinFlow || !isMobile ? '768px' : '100vh',
        maxHeight: '115%',
        backgroundColor: isDarkMode() ? '#020D15' : isMobile ? '#F6F4F4' : 'white',
        border: isMobile ? '1px solid #F6F4F4' : isDarkMode() ? '1px solid #DAE1E9' : 'inherit',

        ...customContent,
      },
    };
  };

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <ModalWrapper
      ariaHideApp={false}
      isOpen={true}
      style={setModalStyle(customStyle)}
      shouldCloseOnOverlayClick={false}
    >
      <CenterYourFaceWrapper
        className="center-face-wrapper"
        style={{
          flexDirection: isMobile ? 'column-reverse' : 'column',
          padding: isMobile ? '20px 15px' : '0',
          boxSizing: 'border-box',
          display: isMobile ? 'none' : 'flex',
        }}
      >
        <SubTitle isJoinFlow={isJoinFlow}>
          Make sure you’re in a well-lit area with a clearly seen face and hold the camera steady. Don’t worry, this
          won’t appear on your profile.{' '}
        </SubTitle>
        <CenterYourFaceText
          isJoinFlow={isJoinFlow}
          style={{
            fontWeight: isMobile ? 600 : 700,
          }}
        >
          {'Center your face'}
        </CenterYourFaceText>
      </CenterYourFaceWrapper>
      <ContentWrapper>
        <FaceLivenessContainer isJoinFlow={isJoinFlow}>
          <SelfieVerification
            theme={theme}
            uid="15f87365-651b-4ec0-8ff9-3649d38d5b1a"
            onFaceLivelinessComplete={handleAnalysisComplete}
            displayText={{
              startScreenBeginCheckText: 'Start Video Check',
              hintCenterFaceText: (
                <>
                  <CenterYourFaceWrapper
                    className="center-face-wrapper"
                    style={{
                      flexDirection: 'column',
                      padding: isMobile ? '10px 15px' : '0',
                      boxSizing: 'border-box',
                      display: isMobile ? 'flex' : 'none',
                    }}
                  >
                    {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389 */}
                    <SubTitle>
                      Make sure you’re in a well-lit area with a clearly seen face and hold the camera steady. Don’t
                      worry, this won’t appear on your profile.{' '}
                    </SubTitle>
                    {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389 */}
                    <CenterYourFaceText
                      style={{
                        fontWeight: isMobile ? 600 : 700,
                      }}
                    >
                      {'Center your face'}
                    </CenterYourFaceText>
                  </CenterYourFaceWrapper>
                </>
              ),
            }}
            onError={error => {
              console.warn('faceliveness error', error);
              dispatch(setFaceLivenessVerifyLoader(false));
              if (!['MOBILE_LANDSCAPE_ERROR'].includes(error?.state)) {
                dispatch(setFaceLivenessStatus({ recommendation: 'ERROR' }));
              }
            }}
            components={{
              ErrorView: () => {
                return null;
              },
            }}
          />

          {(loader || isVerifying) && (
            <LoaderContainer isMobile={isMobile} isJoinFlow={isJoinFlow}>
              <Spinner />
              <p style={{ ...getTextColor().LIGHT_GRAY, fontSize: 20, fontWeight: 400, lineHeight: 'normal' }}>
                Verifying
              </p>
            </LoaderContainer>
          )}
          <BackButtonWrapper>
            <JoinSkipButton
              label={isSVWallByGetSelfieVerified ? 'Cancel' : 'Back'}
              customStyle={{ color: colors.gray['1100'] }}
              onClick={() => {
                dispatch(setFaceLivenessVerifyLoader(false));
                handleBack();
              }}
            />
          </BackButtonWrapper>
        </FaceLivenessContainer>
      </ContentWrapper>
    </ModalWrapper>
  );
};

export default FaceLiveness;
